/**
 * StatsCard molecule - displays key statistics with consistent styling
 * Replaces the existing StatsCard component with enhanced features
 */

import React from 'react';
import {
  HStack,
  VStack,
  useColorModeValue,
  Skeleton,
  Icon,
} from '@chakra-ui/react';
import { Card, CardProps } from '../../atoms/layouts/Card';
import { Text } from '../../atoms/typography/Text';
import { BaseComponentProps, ComponentVariant } from '../../types';

export interface StatsCardProps
  extends BaseComponentProps,
    Omit<CardProps, 'children' | 'variant'> {
  /** Statistics title/label */
  title: string;
  /** Statistics value */
  value: string | number;
  /** Optional subtitle or description */
  subtitle?: string;
  /** Icon to display */
  icon?: React.ComponentType;
  /** Color variant */
  variant?: ComponentVariant;
  /** Whether stats are loading */
  loading?: boolean;
  /** Change percentage (positive/negative) */
  change?: number;
  /** Change period label (e.g., "vs last month") */
  changePeriod?: string;
  /** Whether to show trend indicator */
  showTrend?: boolean;
  /** Trend direction or value */
  trend?: string;
  /** Trend value for additional context */
  trendValue?: string;
}

const variantColors: Record<
  ComponentVariant,
  { bg: [string, string]; color: [string, string] }
> = {
  primary: { bg: ['blue.50', 'blue.900'], color: ['blue.600', 'blue.200'] },
  secondary: { bg: ['gray.50', 'gray.700'], color: ['gray.600', 'gray.300'] },
  success: { bg: ['green.50', 'green.900'], color: ['green.600', 'green.200'] },
  warning: {
    bg: ['orange.50', 'orange.900'],
    color: ['orange.600', 'orange.200'],
  },
  danger: { bg: ['red.50', 'red.900'], color: ['red.600', 'red.200'] },
  info: { bg: ['teal.50', 'teal.900'], color: ['teal.600', 'teal.200'] },
  outline: { bg: ['gray.50', 'gray.700'], color: ['gray.600', 'gray.300'] },
  ghost: {
    bg: ['transparent', 'transparent'],
    color: ['gray.600', 'gray.300'],
  },
};

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  variant = 'primary',
  loading = false,
  change,
  changePeriod,
  showTrend = false,
  className,
  testId,
  ...cardProps
}) => {
  const colors = variantColors[variant];
  const bgColor = useColorModeValue(colors.bg[0], colors.bg[1]);
  const iconColor = useColorModeValue(colors.color[0], colors.color[1]);
  const positiveColor = useColorModeValue('green.600', 'green.300');
  const negativeColor = useColorModeValue('red.600', 'red.300');

  const isPositiveChange = change && change > 0;
  const isNegativeChange = change && change < 0;
  const changeColor = isPositiveChange
    ? positiveColor
    : isNegativeChange
    ? negativeColor
    : 'gray.500';

  return (
    <Card
      variant="elevated"
      padding={6}
      className={className}
      data-testid={testId}
      {...cardProps}
    >
      <HStack spacing={4} align="flex-start">
        {icon && (
          <VStack
            bg={bgColor}
            p={3}
            borderRadius="lg"
            color={iconColor}
            minWidth="fit-content"
          >
            <Icon as={icon} boxSize={6} />
          </VStack>
        )}

        <VStack align="flex-start" spacing={1} flex="1">
          <Text variant="caption">{title}</Text>

          {loading ? (
            <Skeleton height="32px" width="80px" />
          ) : (
            <Text fontSize="2xl" fontWeight="bold">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Text>
          )}

          {subtitle && !loading && <Text variant="caption">{subtitle}</Text>}

          {showTrend && change !== undefined && !loading && (
            <HStack spacing={1}>
              <Text fontSize="sm" color={changeColor} fontWeight="medium">
                {isPositiveChange && '+'}
                {change.toFixed(1)}%
              </Text>
              {changePeriod && <Text variant="caption">{changePeriod}</Text>}
            </HStack>
          )}
        </VStack>
      </HStack>
    </Card>
  );
};
